import { useEffect, useRef } from 'react';
import svgUrl from '../../../assets/matou-presentation.svg';
import birdLogo from '../../../assets/matou-bird-logo-grey.svg';
import textLogo from '../../../assets/matou-text-logo-white.svg';

// Seeded random so boats get consistent-per-session but varied drift params
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return s / 2147483647;
  };
}

function generateDrift(rng: () => number) {
  const duration = 55000 + rng() * 17000; // 55s–72s
  const speed = 0.3 + rng() * 0.7; // 30%–100% of max displacement
  const maxDisplacement = (110 + rng() * 50) * speed;
  const angle = rng() * 2 * Math.PI; // random angle in any direction
  const tx = maxDisplacement * Math.cos(angle);
  const ty = maxDisplacement * Math.sin(angle);
  const delay = rng() * 500;
  return { duration, delay, tx, ty };
}

export default function Hero() {
  const bgRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<{ cancelled: boolean } | null>(null);

  useEffect(() => {
    let ignore = false;

    fetch(svgUrl)
      .then((res) => res.text())
      .then((svgText) => {
        if (ignore || !bgRef.current) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, 'image/svg+xml');
        const svgEl = doc.documentElement;
        bgRef.current.innerHTML = '';
        bgRef.current.appendChild(document.importNode(svgEl, true));

        const svg = bgRef.current.querySelector('svg');
        if (!svg) return;
        svg.style.width = '100%';
        svg.style.height = '100%';
        // Cover the container (zoom + crop sides) instead of letterboxing,
        // so the 16:9 artwork fills tall portrait screens on mobile.
        svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');

        const rng = seededRandom(42);
        const boats: { el: Element; drift: ReturnType<typeof generateDrift> }[] = [];
        for (let i = 0; i <= 30; i++) {
          const el = svg.querySelector(`.matou-boat-${i}`);
          if (!el) continue;
          // Hide every 4th boat + 5 more
          if (i % 4 === 3 || [1, 6, 10, 17, 25].includes(i)) {
            (el as SVGElement).style.display = 'none';
            continue;
          }
          boats.push({ el, drift: generateDrift(rng) });
        }

        const startTime = performance.now();
        animRef.current = { cancelled: false };
        const anim = animRef.current;

        function tick(now: number) {
          if (anim.cancelled) return;
          for (const { el, drift } of boats) {
            const elapsed = now - startTime - drift.delay;
            if (elapsed < 0) continue;
            const t = Math.sin((elapsed / drift.duration) * 2 * Math.PI);
            const x = drift.tx * t;
            const y = drift.ty * t;
            el.setAttribute('transform', `translate(${x}, ${y})`);
          }
          requestAnimationFrame(tick);
        }

        requestAnimationFrame(tick);
      });

    return () => {
      ignore = true;
      if (animRef.current) animRef.current.cancelled = true;
    };
  }, []);

  return (
    <section className="relative bg-matou-teal-dark min-h-screen flex items-center justify-center overflow-hidden">
      <div
        ref={bgRef}
        className="absolute inset-0 opacity-60 pointer-events-none"
      />
      <div className="relative z-10 max-w-content mx-auto px-4 md:px-8 text-center py-20 md:py-32">
        <img src={birdLogo} alt="Mātou Bird" className="w-32 sm:w-40 md:w-48 mx-auto mb-8" />
        <img src={textLogo} alt="Mātou Collective" className="w-40 sm:w-48 md:w-56 mx-auto mb-8" />
        <p className="font-mono text-matou-teal-soft text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
          An indigenous-led technology collective
        </p>
      </div>
    </section>
  );
}
